import { useState } from "react"

import { Button } from "@coval/ui"
import { Trash2 } from "lucide-react"

import { DeleteDialog } from "@/components/modals/DeleteDialog"

import {
  groupingMetricName,
  groupingMetricOptions,
} from "../../utils/groupingMetrics"
import { formatDate } from "../../utils/reportClient"
import { reportScopeCapacity } from "../../utils/reportScopeCapacity"
import type {
  ReportMetric,
  Schedule,
  ScopeLimits,
} from "../../utils/reportTypes"
import { reportClass } from "./ReportStyles"

const CADENCE_LABELS: Record<string, string> = {
  daily: "Every day",
  weekly: "Every week",
  monthly: "Every month",
}

export function ReportSchedules({
  schedules,
  disabled,
  onRun,
  onOpen,
  onDelete,
  onSetEnabled,
  automaticScheduling = true,
  metrics = [],
  scopeLimits,
}: {
  schedules: Schedule[]
  disabled?: boolean
  onRun?: (id: string) => void
  onOpen?: (notebookId: string) => void
  onDelete?: (id: string) => void
  onSetEnabled?: (schedule: Schedule, enabled: boolean) => void
  automaticScheduling?: boolean
  metrics?: ReportMetric[]
  scopeLimits?: ScopeLimits
}) {
  const [pendingDelete, setPendingDelete] = useState<Schedule | null>(null)
  // A saved recipe can name a metric that is no longer groupable; resolve
  // against the same filtered catalog the configuration uses so both
  // surfaces agree it is unavailable.
  const groupingMetrics = groupingMetricOptions(metrics)
  return (
    <>
      {schedules.length > 0 && (
        <section className={reportClass("saved-schedules")}>
          <h3>Schedules</h3>
          {schedules.map((item) => {
            const savedScope = item.scope ?? item.recipe?.scope
            const manual =
              !automaticScheduling ||
              item.execution_mode === "manual" ||
              item.status === "manual_only"
            const capacityError = savedScope
              ? reportScopeCapacity(savedScope, scopeLimits).error
              : null
            const lastRun = item.last_run ?? undefined
            const nextRun =
              !manual && item.enabled && item.next_run_at
                ? ` · next run ${formatDate(item.next_run_at, true)}`
                : ""
            const groupedBy = savedScope?.group_by_metric_id
              ? (groupingMetricName(
                  groupingMetrics,
                  savedScope.group_by_metric_id,
                ) ?? "a metric no longer available")
              : undefined
            return (
              <p key={item.id}>
                <strong>{item.name}</strong>
                <br />
                <span>
                  {CADENCE_LABELS[item.cadence] ?? item.cadence} ·{" "}
                  {manual
                    ? "Manual generation"
                    : item.enabled
                      ? "Enabled"
                      : "Paused"}
                  {nextRun}
                  {groupedBy ? ` · grouped by ${groupedBy}` : ""}
                </span>
                {lastRun && (
                  <span className={reportClass("field-hint")}>
                    {lastRun.status === "complete"
                      ? `Last notebook ${formatDate(lastRun.at)}`
                      : `Last run ${lastRun.status}${lastRun.error ? `: ${lastRun.error}` : ""}`}
                    {lastRun.status === "complete" &&
                      lastRun.notebook_id &&
                      onOpen && (
                        <Button
                          variant="ghost"
                          disabled={disabled}
                          onClick={() => onOpen(lastRun.notebook_id ?? "")}
                        >
                          Open
                        </Button>
                      )}
                  </span>
                )}
                {manual && onRun && (
                  <Button
                    variant="secondary"
                    disabled={disabled || Boolean(capacityError)}
                    onClick={() => onRun(item.id)}
                  >
                    Generate saved recipe
                  </Button>
                )}
                {manual && onRun && capacityError && (
                  <span className={reportClass("field-hint")}>
                    {capacityError}
                  </span>
                )}
                {automaticScheduling &&
                  onSetEnabled &&
                  item.updated_at &&
                  ["daily", "weekly", "monthly"].includes(item.cadence) &&
                  !item.period_start &&
                  !item.period_end && (
                    <Button
                      variant="secondary"
                      disabled={
                        disabled || (!item.enabled && Boolean(capacityError))
                      }
                      onClick={() => onSetEnabled(item, !item.enabled)}
                    >
                      {item.enabled
                        ? "Pause automatic runs"
                        : "Enable automatic runs"}
                    </Button>
                  )}
                {onDelete && (
                  <Button
                    variant="ghost"
                    disabled={disabled}
                    aria-label={`Delete recipe ${item.name}`}
                    onClick={() => setPendingDelete(item)}
                  >
                    <Trash2 aria-hidden="true" />
                    Delete
                  </Button>
                )}
              </p>
            )
          })}
        </section>
      )}
      {onDelete && (
        <DeleteDialog
          open={pendingDelete !== null}
          onOpenChange={(open) => {
            if (!open) {
              setPendingDelete(null)
            }
          }}
          onConfirm={() => {
            const target = pendingDelete
            setPendingDelete(null)
            if (target) {
              onDelete(target.id)
            }
          }}
          title="Delete recipe"
          prompt={
            pendingDelete
              ? `Are you sure you want to delete "${pendingDelete.name}"?`
              : ""
          }
          note="This cannot be undone. Saved notebooks are unchanged."
          confirmTestId="recipe-delete-confirm-button"
        />
      )}
    </>
  )
}
